import type { WorldDocument, WorldEvent, WorldTransaction } from "@private-fiction/story-core";
import { runtimeConfig } from "../config/runtimeConfig";
import { applyWorldTransaction, normalizeWorldRuntimeState } from "@private-fiction/story-core";
import { recordSyncFailure } from "./syncFailure";
import { bootstrapTransactions, canRecoverLegacyMetadata, canRecoverSessionSchema, WORLD_SYNC_VERSION, worldDeliveryHold, type WorldDeliveryState } from "./worldSyncPolicy";

const DATABASE = `private-fiction-world-v1:${runtimeConfig.supabaseUrl || "offline"}`;
const LEGACY_DOCUMENTS = "documents";
const DOCUMENTS = "world-documents";
const OUTBOX = "story-outbox";
const EVENTS = "world-events";
const METADATA = "runtime-metadata";
const VERSION = 3;

interface StoredDocument extends WorldDocument { storageKey: string }
interface StoredWorldEvent extends WorldEvent { storageKey: string; transactionKey: string }
export interface PendingStoryTransaction extends WorldDeliveryState {
  id: string;
  storyId: string;
  transaction: WorldTransaction;
  queuedAt: string;
  queueOrder?: number;
  attempts: number;
  lastError?: string;
  lastErrorCode?: string;
  blocked?: boolean;
  nextAttemptAt?: number;
}

const documentKey = (storyId: string, documentId: string) => `${storyId}\u0000${documentId}`;
const eventKey = (event: Pick<WorldEvent, "storyId" | "transactionId" | "eventIndex">) => `${event.storyId}\u0000${event.transactionId}\u0000${event.eventIndex}`;
const transactionKey = (storyId: string, transactionId: string) => `${storyId}\u0000${transactionId}`;
const toStoredDocument = (document: WorldDocument): StoredDocument => ({ ...structuredClone(document), storageKey: documentKey(document.storyId, document.id) });
const fromStoredDocument = ({ storageKey: _storageKey, ...document }: StoredDocument): WorldDocument => normalizeWorldRuntimeState(document);
const toStoredEvent = (event: WorldEvent): StoredWorldEvent => ({ ...structuredClone(event), storageKey: eventKey(event), transactionKey: transactionKey(event.storyId, event.transactionId) });
const fromStoredEvent = ({ storageKey: _storageKey, transactionKey: _transactionKey, ...event }: StoredWorldEvent): WorldEvent => event;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const upgrade = request.transaction!;
      const documents = database.objectStoreNames.contains(DOCUMENTS)
        ? upgrade.objectStore(DOCUMENTS)
        : database.createObjectStore(DOCUMENTS, { keyPath: "storageKey" });
      if (!documents.indexNames.contains("storyId")) documents.createIndex("storyId", "storyId", { unique: false });
      const outbox = database.objectStoreNames.contains(OUTBOX)
        ? upgrade.objectStore(OUTBOX)
        : database.createObjectStore(OUTBOX, { keyPath: "id" });
      if (!outbox.indexNames.contains("storyId")) outbox.createIndex("storyId", "storyId", { unique: false });
      const events = database.objectStoreNames.contains(EVENTS)
        ? upgrade.objectStore(EVENTS)
        : database.createObjectStore(EVENTS, { keyPath: "storageKey" });
      if (!events.indexNames.contains("storyId")) events.createIndex("storyId", "storyId", { unique: false });
      if (!events.indexNames.contains("transactionKey")) events.createIndex("transactionKey", "transactionKey", { unique: false });
      if (!database.objectStoreNames.contains(METADATA)) database.createObjectStore(METADATA, { keyPath: "key" });

      if (database.objectStoreNames.contains(LEGACY_DOCUMENTS)) {
        const legacy = upgrade.objectStore(LEGACY_DOCUMENTS).getAll();
        legacy.onsuccess = () => (legacy.result as WorldDocument[]).forEach((document) => documents.put(toStoredDocument(document)));
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadStoryDocuments(storyId: string): Promise<WorldDocument[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(DOCUMENTS, "readonly").objectStore(DOCUMENTS).index("storyId").getAll(storyId);
    request.onsuccess = () => resolve((request.result as StoredDocument[]).map(fromStoredDocument));
    request.onerror = () => reject(request.error);
  });
}

export async function seedStoryDocuments(storyId: string, fallback: readonly WorldDocument[], initialize?: { syncTarget?: string }): Promise<WorldDocument[]> {
  const initial = fallback.filter((document) => document.storyId === storyId);
  const creates = initialize ? bootstrapTransactions(storyId, initial) : [];
  const database = await openDatabase();
  let result: WorldDocument[] = [];
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction([DOCUMENTS, OUTBOX], "readwrite");
    const store = write.objectStore(DOCUMENTS);
    // Check and seed under the same IDB transaction, including across tabs.
    const saved = store.index("storyId").getAll(storyId);
    saved.onsuccess = () => {
      result = (saved.result as StoredDocument[]).map(fromStoredDocument);
      if (result.length) return;
      result = initial.map(document => structuredClone(document));
      initial.forEach((document) => store.put(toStoredDocument(document)));
      creates.forEach((transaction, index) => write.objectStore(OUTBOX).add({
        id: transaction.id, storyId, transaction, queuedAt: transaction.createdAt, attempts: 0,
        syncVersion: WORLD_SYNC_VERSION, queueOrder: index + 1, ...initialize,
      } satisfies PendingStoryTransaction));
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
    write.onabort = () => reject(write.error ?? new Error("Session initialization aborted"));
  });
  return result;
}

export async function commitLocalTransaction(_current: readonly WorldDocument[], transaction: WorldTransaction, syncTarget?: string): Promise<WorldDocument[]> {
  if (!transaction.mutations.length || transaction.mutations.length > 64) throw new Error("Transaction must contain 1..64 mutations");
  const database = await openDatabase();
  let result: WorldDocument[] = [];
  let failure: unknown;
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction([DOCUMENTS, EVENTS, OUTBOX], "readwrite");
    const documents = write.objectStore(DOCUMENTS);
    const events = write.objectStore(EVENTS);
    const queued = write.objectStore(OUTBOX).index("storyId").getAll(transaction.storyId);
    const prior=events.index('transactionKey').getAll(transactionKey(transaction.storyId,transaction.id));
    const saved = documents.index("storyId").getAll(transaction.storyId);
    saved.onsuccess = () => {
      try {
        // Validate the persisted revision, not a stale React snapshot. Concurrent
        // IDB writers serialize this read + compare + documents/events/outbox write.
        const persisted = (saved.result as StoredDocument[]).map(fromStoredDocument);
        const previous=prior.result as StoredWorldEvent[];
        if(previous.length) {
          // JSONB checkpoints reorder object keys. Compare data recursively,
          // keeping array order and all values significant for replay safety.
          const comparable=(value:WorldDocument)=>JSON.stringify({...value,revision:0,createdAt:'',updatedAt:''},(_key,item)=>
            item && typeof item==='object' && !Array.isArray(item) ? Object.fromEntries(Object.keys(item).sort().map(key=>[key,item[key]])) : item);
          const same=previous.length===transaction.mutations.length && transaction.mutations.every(mutation=>previous.some(event=>event.documentId===mutation.documentId && event.operation===mutation.operation && event.beforeRevision===mutation.expectedRevision && (!mutation.document || event.after && comparable(event.after)===comparable(mutation.document))));
          if(!same) throw new Error('Transaction id was already used for a different change');
          result=persisted;return;
        }
        for (const mutation of transaction.mutations) {
          const exists = persisted.some(document => document.id === mutation.documentId);
          if ((mutation.operation === "create") === exists) throw new Error(`Operation does not match stored document: ${mutation.documentId}`);
          if (mutation.document && mutation.document.storyId !== transaction.storyId) throw new Error("Transaction cannot write another story's document");
        }
        const applied = applyWorldTransaction(persisted, transaction);
        applied.events.forEach(event => { documents.put(toStoredDocument(event.after!)); events.put(toStoredEvent(event)); });
        write.objectStore(OUTBOX).add({
          id: transaction.id, storyId: transaction.storyId, transaction: structuredClone(transaction),
          queuedAt: new Date().toISOString(), attempts: 0, syncVersion: WORLD_SYNC_VERSION,
          queueOrder: Math.max(0, ...(queued.result as PendingStoryTransaction[]).map(item => item.queueOrder ?? 0)) + 1,
          ...(syncTarget ? { syncTarget } : {}),
        } satisfies PendingStoryTransaction);
        result = applied.documents;
      } catch (error) { failure = error; write.abort(); }
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
    write.onabort = () => reject(failure ?? write.error ?? new Error("Local transaction aborted"));
  });
  return result;
}

export async function loadStoryEvents(storyId: string): Promise<WorldEvent[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(EVENTS, "readonly").objectStore(EVENTS).index("storyId").getAll(storyId);
    request.onsuccess = () => resolve((request.result as StoredWorldEvent[]).map(fromStoredEvent).sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.eventIndex - right.eventIndex));
    request.onerror = () => reject(request.error);
  });
}

export async function loadTransactionEvents(storyId: string, transactionId: string): Promise<WorldEvent[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(EVENTS, "readonly").objectStore(EVENTS).index("transactionKey").getAll(transactionKey(storyId, transactionId));
    request.onsuccess = () => resolve((request.result as StoredWorldEvent[]).map(fromStoredEvent).sort((left, right) => left.eventIndex - right.eventIndex));
    request.onerror = () => reject(request.error);
  });
}

export async function listPendingStoryTransactions(storyId?: string): Promise<PendingStoryTransaction[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const store = database.transaction(OUTBOX, "readonly").objectStore(OUTBOX);
    const request = storyId ? store.index("storyId").getAll(storyId) : store.getAll();
    request.onsuccess = () => resolve((request.result as PendingStoryTransaction[]).sort((a, b) => a.storyId === b.storyId && a.queueOrder !== undefined && b.queueOrder !== undefined ? a.queueOrder - b.queueOrder : a.queuedAt.localeCompare(b.queuedAt)));
    request.onerror = () => reject(request.error);
  });
}

export async function acknowledgeStoryTransaction(transactionId: string): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(OUTBOX, "readwrite");
    write.objectStore(OUTBOX).delete(transactionId);
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
  });
}

/** Persist before dispatch: a reload must not silently replay an unknown result. */
export async function claimStoryTransaction(transactionId: string, target: string): Promise<boolean> {
  const database = await openDatabase();
  let claimed = false;
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(OUTBOX, "readwrite");
    const store = write.objectStore(OUTBOX);
    const current = store.get(transactionId);
    current.onsuccess = () => {
      const item = current.result as PendingStoryTransaction | undefined;
      if (!item || worldDeliveryHold(item, target) || (item.nextAttemptAt ?? 0) > Date.now()) return;
      store.put({ ...item, deliveryState: "sending" });
      claimed = true;
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
    write.onabort = () => reject(write.error ?? new Error("Dispatch claim aborted"));
  });
  return claimed;
}

export async function noteStoryTransactionFailure(pending: PendingStoryTransaction, error: unknown): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(OUTBOX, "readwrite");
    const store = write.objectStore(OUTBOX);
    const current = store.get(pending.id);
    current.onsuccess = () => {
      if (current.result) store.put({ ...current.result, ...recordSyncFailure(current.result, error), deliveryState: "pending" });
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
  });
}

export async function recoverSessionSchemaTransactions(storyId: string, target: string, fileTypeIds: readonly string[], fieldKeys?: Record<string, string[]>): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(OUTBOX, "readwrite");
    const store = write.objectStore(OUTBOX);
    const request = store.getAll();
    request.onsuccess = () => {
      for (const item of request.result as PendingStoryTransaction[]) {
        const metadata = Boolean(fieldKeys && canRecoverLegacyMetadata(item, target, fieldKeys));
        if (item.storyId !== storyId || (!metadata && !canRecoverSessionSchema(item, target, fileTypeIds))) continue;
        // Retain the exact transaction and destination. No unknown-result,
        // conflict, or other failing outbox item is released by this repair.
        store.put({ ...item, attempts: 0, blocked: false, nextAttemptAt: 0,
          lastError: undefined, lastErrorCode: undefined,
          ...(metadata ? { metadataRecoveryAt: new Date().toISOString() } : { schemaRecoveryAt: new Date().toISOString() }) });
      }
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
    write.onabort = () => reject(write.error ?? new Error("Schema recovery aborted"));
  });
}

export async function mergeRemoteStoryDocuments(documents: readonly WorldDocument[]): Promise<void> {
  if (!documents.length) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction([DOCUMENTS, OUTBOX], "readwrite");
    const store = write.objectStore(DOCUMENTS);
    // Pull pages span all stories. Never overwrite another clone's unsent edits
    // while syncing the currently selected story, or regress a newer revision.
    const pending = write.objectStore(OUTBOX).getAll();
    pending.onsuccess = () => {
      const edits = pending.result as PendingStoryTransaction[];
      for (const document of documents) {
        const protectedLocally = edits.some(edit => edit.storyId === document.storyId && edit.transaction.mutations.some(mutation => mutation.documentId === document.id));
        if (protectedLocally) continue;
        const current = store.get(documentKey(document.storyId, document.id));
        current.onsuccess = () => {
          if (!current.result || current.result.revision < document.revision) store.put(toStoredDocument(normalizeWorldRuntimeState(document)));
        };
      }
    };
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
  });
}

export async function mergeRemoteStoryEvents(events: readonly WorldEvent[]): Promise<void> {
  if (!events.length) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(EVENTS, "readwrite");
    const store = write.objectStore(EVENTS);
    events.forEach((event) => store.put(toStoredEvent(event)));
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
  });
}

export async function getRuntimeCursor(name: string): Promise<number> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(METADATA, "readonly").objectStore(METADATA).get(name);
    request.onsuccess = () => resolve(Number(request.result?.value ?? 0));
    request.onerror = () => reject(request.error);
  });
}

export async function setRuntimeCursor(name: string, value: number): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const write = database.transaction(METADATA, "readwrite");
    write.objectStore(METADATA).put({ key: name, value });
    write.oncomplete = () => resolve();
    write.onerror = () => reject(write.error);
  });
}
